import type { TFunction } from 'i18next';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import DetailsDrawerIcon from '../../assets/icons/details-drawer.svg?react';
import type { PatientResponse } from '../../api/generated';
import { tertiaryIconButtonStyle } from '../UI';
import type { TableColumn } from '../UI';
import { Button } from '../UI/base/Button';

interface PatientColumnCallbacks {
  onViewDetails: (patient: PatientResponse) => void;
  onDelete: (patient: PatientResponse) => void;
  // Deleting a patient is a patient-booker action; the column is omitted otherwise.
  canDelete: boolean;
}

export const getPatientColumns = (
  t: TFunction,
  { onViewDetails, onDelete, canDelete }: PatientColumnCallbacks
): TableColumn<PatientResponse>[] => [
  {
    key: 'name',
    header: t('patients-col-name'),
    sortable: true,
    sortValue: (patient: PatientResponse) => patient.name,
    render: (patient: PatientResponse) => patient.name
  },
  {
    key: 'cpr',
    header: t('patients-col-cpr'),
    sortable: true,
    sortValue: (patient: PatientResponse) => patient.cprNumber,
    render: (patient: PatientResponse) => patient.cprNumber
  },
  {
    key: 'latestIntervention',
    header: t('patients-col-latest-intervention'),
    sortable: true,
    // No data source yet — sorting is enabled but ordering is a no-op until a
    // latest-intervention date is available on the patient.
    sortValue: () => '',
    render: () => '-'
  },
  {
    key: 'details',
    header: t('patients-col-details'),
    width: 140,
    headerAlign: 'center',
    cellStyle: { alignItems: 'center' },
    render: (patient: PatientResponse) => (
      <Button
        type="tertiary"
        onClick={() => onViewDetails(patient)}
        styles={{ root: tertiaryIconButtonStyle }}
      >
        <DetailsDrawerIcon />
      </Button>
    )
  },
  ...(canDelete
    ? [
        {
          key: 'delete',
          header: t('settings-col-delete'),
          width: 140,
          headerAlign: 'center' as const,
          cellStyle: { alignItems: 'center' },
          render: (patient: PatientResponse) => (
            <Button
              type="tertiary"
              onClick={() => onDelete(patient)}
              styles={{ root: tertiaryIconButtonStyle }}
            >
              <DeleteIcon style={{ fill: 'var(--color-interaction-primary)' }} />
            </Button>
          )
        }
      ]
    : [])
];
